import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { useT } from 'client/i18n/hooks/useT';
import Icon from '../../../shared/Icon';
import { IconType } from '../../../shared/Icon/types';
import { ConnectDots } from '../ConnectDots';
import { Participant } from '../index';
import { SubscribeError } from '../types/errors';

type Props = Omit<Participant, 'podcastName'> & {
  session: any;
  stream: any;
  handleSubscribe: (isSubsribed: boolean) => void;
};
type SubscribeStatus = 'notInitialized' | 'subscribing' | 'success' | 'fail';

export function Caller({
  userId,
  deviceKind,
  displayName,
  isHost,
  isSelf,
  uuid,
  stream,
  session,
  handleSubscribe,
}: Props) {
  const t = useT();
  const [subscribeStatus, setSubscribeStatus] = useState<SubscribeStatus>(
    'notInitialized'
  );
  const retryTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  function subscribe() {
    setSubscribeStatus('subscribing');
    session.subscribe(stream, 'subscriber', {}, (err: SubscribeError) => {
      if (err) {
        Sentry.captureMessage(
          `subscriber error: Unable to subscribe to UUID: ${uuid}`
        );
        handleSubscribe(false);
        setSubscribeStatus('fail');
        retryTimeoutId.current = setTimeout(subscribe, 5000);
      } else {
        handleSubscribe(true);
        setSubscribeStatus('success');
      }
    });
  }

  useEffect(() => {
    // only subscribe to streams that are not yourself
    if (!isSelf) subscribe();
    return () => {
      if (retryTimeoutId.current !== null) {
        clearTimeout(retryTimeoutId.current);
      }
    };
  }, [isSelf]);

  function getConnectLabel() {
    if (subscribeStatus !== 'success') {
      return (
        <>
          <ConnectedLabelWrapper>
            {t(
              'CallJoin.Caller.ConnectingLabel',
              'Connecting',
              'Call with friends feature, connecting to call label'
            )}
          </ConnectedLabelWrapper>
          <ConnectDots type="connecting" />
        </>
      );
    }
    return (
      <>
        <ConnectedLabelWrapper>
          {t(
            'CallJoin.Caller.ConnectedLabel',
            'Connected',
            'Call with friends feature, connected to call label'
          )}
        </ConnectedLabelWrapper>
        <ConnectDots type="connected" />
      </>
    );
  }

  function getSubText() {
    if (isHost && isSelf)
      return t(
        'CallJoin.Caller.HostLabel',
        'Host',
        'Call with friends feature, who is the caller, host label'
      );
    if (isHost) {
      return (
        <>
          {t(
            'CallJoin.Caller.HostLabel',
            'Host',
            'Call with friends feature, who is the caller, host label'
          )}
          <SeparatorDot />
          {getConnectLabel()}
        </>
      );
    }
    if (isSelf)
      return t(
        'CallJoin.Caller.SelfLabel',
        'You',
        'Call with friends feature, who is the caller, self label'
      );
    return getConnectLabel();
  }

  const { fillColor, iconType, padding } = getIconProperties({
    userId,
    deviceKind,
  });

  return (
    <Container>
      <IconWrapper fillColor={fillColor} padding={padding}>
        {iconType !== null ? (
          <Icon type={iconType} fillColor="white" />
        ) : (
          <div />
        )}
      </IconWrapper>
      <NameContainer>
        <DisplayName>{displayName}</DisplayName>
        <SubTextWrapper>{getSubText()}</SubTextWrapper>
      </NameContainer>
    </Container>
  );
}

const SubTextWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ConnectedLabelWrapper = styled.span`
  display: inline-block;
  margin-right: 6px;
`;

const SeparatorDot = styled.span`
  display: inline-block;
  width: 3px;
  height: 3px;
  margin: 0 4px;
  border-radius: 100%;
  background-color: #292f36;
`;

const NameContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 24px;
`;

const DisplayName = styled.h4`
  margin: 0;
  overflow: hidden;
  color: #292f36;
  font-weight: bold;
  font-size: 1.375rem;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Container = styled.div`
  display: flex;
  margin-bottom: 20px;
  padding-right: 20px;
`;

type FillColor = '#5000b9' | '#5499FE' | '#2CCFB2' | 'white';
type IconWrapperProps = {
  fillColor: FillColor;
  padding: string[];
};

const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  flex: 0 0 48px;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: ${(props: IconWrapperProps) => props.padding[0]};
  border-radius: 100%;
  background-color: ${(props: IconWrapperProps) => props.fillColor};
  @media (max-width: 600px) {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    padding: ${(props: IconWrapperProps) => props.padding[1]};
  }
`;

function getIconProperties({
  userId,
  deviceKind,
}: Pick<Participant, 'userId' | 'deviceKind'>): {
  fillColor: FillColor;
  iconType: IconType | null;
  padding: string[];
} {
  if (userId) {
    return {
      fillColor: '#5000b9',
      iconType: 'anchor_logo',
      padding: ['14px', '12px'],
    };
  }
  if (deviceKind === 'mobile') {
    return {
      fillColor: '#5499FE',
      iconType: 'PhoneIcon',
      padding: ['16px', '14px'],
    };
  }
  if (deviceKind === 'desktop') {
    return {
      fillColor: '#2CCFB2',
      iconType: 'LaptopIcon',
      padding: ['14px', '12px'],
    };
  }
  return {
    fillColor: 'white',
    iconType: null,
    padding: ['0px', '0px'],
  };
}
